import { discardRecoverableRecording } from "~/features/recording-recovery/utils/local-recording-actions";
import {
    getRecoverableRecordingKey,
    notifyRecoverableRecordingsChanged,
    type RecoverableRecording,
} from "~/hooks/media/recoverable-recordings";
import { logger } from "~/lib/logger";

export interface ReconcileEntry {
    key: string;
    uploadSessionId?: string | null;
    startedAt: string;
    elapsedMs: number;
    clientId?: string | null;
}

export interface ReconcileSession {
    id: string;
    status: string;
    hasMedia: boolean;
    meetingStartTime: string;
    meetingDuration: number | null;
    clientId: string | null;
}

export interface ReconcileResult {
    key: string;
    match: "exact" | "probable" | "none";
    alreadyUploaded: boolean;
    session?: ReconcileSession;
}

export interface RecordingReconciliation {
    match: "probable";
    sessionId: string;
    sessionDate: string;
    sessionDurationSeconds: number | null;
}

export interface ReconciledRecoverableRecording extends RecoverableRecording {
    reconciliation?: RecordingReconciliation;
}

interface ReconcileApiClient {
    post: (url: string, body?: Record<string, unknown>) => Promise<Response>;
}

export function buildReconcileEntries(
    recordings: RecoverableRecording[],
): ReconcileEntry[] {
    return recordings.slice(0, 200).map((recording) => ({
        key: getRecoverableRecordingKey(recording),
        uploadSessionId: recording.durableMeta?.uploadSessionId ?? null,
        startedAt: recording.durableMeta?.startedAt ?? recording.createdAt,
        elapsedMs: recording.elapsedMs,
        clientId: recording.clientId ?? null,
    }));
}

// AIDEV-NOTE: useRecoverableRecordings mounts in several components at once
// (banner, sidebar pip, mobile tab bar), each reconciling on mount. Dedupe
// identical batches through a short-lived shared promise so one page load
// fires one POST, while a changed recording list (new signature) refetches.
const RECONCILE_CACHE_MS = 15_000;
let cachedSignature: string | null = null;
let cachedAt = 0;
let cachedResult: Promise<Map<string, ReconcileResult>> | null = null;

export function resetReconcileCacheForTests(): void {
    cachedSignature = null;
    cachedAt = 0;
    cachedResult = null;
}

export async function reconcileRecordings(
    apiClient: ReconcileApiClient,
    recordings: RecoverableRecording[],
    // AIDEV-NOTE: Cache is scoped by user AND the full entry payload — on a
    // shared browser an account switch must never reuse the previous user's
    // results, and a newly stamped uploadSessionId must invalidate.
    scopeUserId: string | null = null,
): Promise<Map<string, ReconcileResult>> {
    if (recordings.length === 0) return new Map();

    const signature = `${scopeUserId ?? "anon"}::${JSON.stringify(
        buildReconcileEntries(recordings),
    )}`;
    if (
        cachedResult &&
        cachedSignature === signature &&
        Date.now() - cachedAt < RECONCILE_CACHE_MS
    ) {
        return cachedResult;
    }

    cachedSignature = signature;
    cachedAt = Date.now();
    // AIDEV-NOTE: Failures are never cached — a transient error must not
    // suppress reconciliation for other hook instances for the TTL window.
    const requestSignature = signature;
    const pending = fetchReconcileResults(apiClient, recordings).then(
        (outcome) => {
            if (!outcome.ok && cachedSignature === requestSignature) {
                cachedResult = null;
                cachedSignature = null;
            }
            return outcome.results;
        },
    );
    cachedResult = pending;
    return pending;
}

async function fetchReconcileResults(
    apiClient: ReconcileApiClient,
    recordings: RecoverableRecording[],
): Promise<{ ok: boolean; results: Map<string, ReconcileResult> }> {
    try {
        const response = await apiClient.post("/recordings/reconcile", {
            entries: buildReconcileEntries(recordings),
        });
        if (!response.ok) return { ok: false, results: new Map() };

        const payload = (await response.json()) as { results?: unknown };
        if (!Array.isArray(payload.results)) {
            return { ok: false, results: new Map() };
        }

        return {
            ok: true,
            results: new Map(
                (payload.results as ReconcileResult[]).map((result) => [
                    result.key,
                    result,
                ]),
            ),
        };
    } catch (error) {
        // AIDEV-NOTE: Reconciliation is fail-open; storage entries stay visible on every API/auth failure.
        logger
            .withError(error as Error)
            .warn("Failed to reconcile recoverable recordings");
        return { ok: false, results: new Map() };
    }
}

export async function purgeExactUploadedRecordings(
    recordings: RecoverableRecording[],
    results: Map<string, ReconcileResult>,
): Promise<Set<string>> {
    const purgedKeys = new Set<string>();

    // AIDEV-NOTE: Only exact uploaded twins are auto-purged; probable matches always require confirmation.
    // A timed-out/failed durable delete still counts as purged: the id is
    // pending-tracked and hidden from every later listing.
    for (const recording of recordings) {
        const key = getRecoverableRecordingKey(recording);
        const result = results.get(key);
        if (result?.match !== "exact" || !result.alreadyUploaded) continue;

        try {
            await discardRecoverableRecording(recording, { notify: false });
            purgedKeys.add(key);
        } catch (error) {
            logger
                .withError(error as Error)
                .warn("Failed to purge exact uploaded recording twin");
        }
    }

    if (purgedKeys.size > 0) notifyRecoverableRecordingsChanged();
    return purgedKeys;
}

export function applyReconciliationResults(
    recordings: RecoverableRecording[],
    results: Map<string, ReconcileResult>,
    purgedKeys: Set<string> = new Set(),
): ReconciledRecoverableRecording[] {
    return recordings
        .filter(
            (recording) =>
                !purgedKeys.has(getRecoverableRecordingKey(recording)),
        )
        .map((recording) => {
            const result = results.get(getRecoverableRecordingKey(recording));
            if (
                result?.match !== "probable" ||
                !result.alreadyUploaded ||
                !result.session
            ) {
                return recording;
            }

            return {
                ...recording,
                reconciliation: {
                    match: "probable" as const,
                    sessionId: result.session.id,
                    sessionDate: result.session.meetingStartTime,
                    sessionDurationSeconds: result.session.meetingDuration,
                },
            };
        });
}

export function splitReconciledRecordings(
    recordings: ReconciledRecoverableRecording[],
): {
    unmatched: ReconciledRecoverableRecording[];
    alreadyUploaded: ReconciledRecoverableRecording[];
} {
    return {
        unmatched: recordings.filter((recording) => !recording.reconciliation),
        alreadyUploaded: recordings.filter(
            (recording) => recording.reconciliation !== undefined,
        ),
    };
}
